import React from 'react';
import moment from 'moment';
import { SkeletonLoader } from '../../../../components/SkeletonLoader';
import { Pill, PillVariant } from '../../../../components/Pill';
import styles from './AuditTrail.module.css';

const ActionTypeEnum = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete'
} as const;

interface AuditTrailEntry {
  actioned_by_name: string;
  date: string;
  action: string;
  updated_fields: Array<{
    name: string;
    value?: string;
    old_value?: string;
    new_value?: string;
  }>;
}

interface AuditTrailProps {
  data: AuditTrailEntry[];
  loading?: boolean;
  fieldLabels?: Record<string, string>;
}

const AuditTrail: React.FC<AuditTrailProps> = ({ data, loading, fieldLabels }) => {
  const getActionVariant = (action: string): PillVariant => {
    const normalizedAction = action.toLowerCase().replace(/d$/, ''); // Remove trailing 'd' if present

    switch (normalizedAction) {
      case ActionTypeEnum.CREATE:
        return PillVariant.Success;
      case ActionTypeEnum.UPDATE:
        return PillVariant.Warning;
      case ActionTypeEnum.DELETE:
        return PillVariant.Danger;
      default:
        return PillVariant.Success;
    }
  };

  const renderValue = (value: string) => (
    <span className={styles.newValue}>&quot;{value}&quot;</span>
  );

  const formatChanges = (updatedFields: AuditTrailEntry['updated_fields'], action: string) => {
    if (action === 'Deleted' || updatedFields.length === 0) {
      return '-';
    }

    return updatedFields.map((field) => {
      const fieldName = fieldLabels?.[field.name] ?? (field.name.replace(/_/g, ' ').charAt(0).toUpperCase() + field.name.replace(/_/g, ' ').slice(1));

      const value = field.new_value ?? field.value;
      if (value !== undefined && value !== null) {
        return (
          <div key={`${field.name}-${String(value ?? field.old_value ?? '')}`} className={styles.changeItem}>
            <span className={styles.fieldName}>{fieldName}:</span>
            {renderValue(value)}
          </div>
        );
      }
      return null;
    }).filter(Boolean);
  };

  const renderSkeletonRows = () => Array.from({ length: 3 }, (_, index) => (
      <tr key={`skeleton-${index}`}>
        <td className={styles.dateCell}>
          <SkeletonLoader width="80px" height="14px" aria-label="Loading date" />
        </td>
        <td className={styles.actionCell}>
          <SkeletonLoader width="60px" height="20px" aria-label="Loading action" />
        </td>
        <td className={styles.changesCell}>
          <SkeletonLoader width="90%" height="14px" aria-label="Loading changes" />
        </td>
        <td className={styles.actionerCell}>
          <SkeletonLoader width="70px" height="14px" aria-label="Loading actioner" />
        </td>
      </tr>
    ));

  if (loading) {
    return (
      <div>
        <div className={styles.auditHeader}>Change History</div>
        <div className={styles.auditContainer}>
          <div className={styles.tableContainer}>
            <table className={styles.auditTable}>
              <thead className={styles.stickyHeader}>
                <tr>
                  <th className={styles.firstHeader}>Date</th>
                  <th style={{textAlign: 'center'}}>Action</th>
                  <th>Changes</th>
                  <th>Actioner</th>
                </tr>
              </thead>
              <tbody>
                {renderSkeletonRows()}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.auditHeader}>Change History</div>
      <div className={styles.auditContainer}>
        <div className={styles.tableContainer}>
          <table className={styles.auditTable}>
            <thead className={styles.stickyHeader}>
              <tr>
                <th className={styles.firstHeader}>Date</th>
                <th style={{textAlign: 'center'}}>Action</th>
                <th>Changes</th>
                <th>Actioner</th>
              </tr>
            </thead>
            <tbody>
              {data.map((entry) => (
                <tr key={`${entry.date}-${entry.action}-${entry.actioned_by_name}`}>
                  <td className={styles.dateCell}>
                    {moment(entry.date).format('DD MMM YY HH:mm')}
                  </td>
                  <td className={styles.actionCell}>
                    <Pill variant={getActionVariant(entry.action)}>
                      {entry.action}
                    </Pill>
                  </td>
                  <td className={styles.changesCell}>
                    {formatChanges(entry.updated_fields, entry.action)}
                  </td>
                  <td className={styles.actionerCell}>
                    {entry.actioned_by_name}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AuditTrail;